import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarHeart,
  CheckCircle,
  XCircle,
  Loader2,
  User,
  Phone,
  Mail,
  Banknote,
  Calendar,
  Clock,
  Bell,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  CircleDollarSign,
  Pencil,
} from 'lucide-react';
import { apiFetch } from '../lib/api';

interface MonthlyPledge {
  id: number;
  pledgeNum: string;
  donorName: string;
  donorPhone: string;
  donorEmail?: string;
  monthlyAmount: number;
  paymentMethod: string;
  status: 'active' | 'paused' | 'cancelled' | 'pending';
  startDate: string;
  nextReminderDate?: string;
  totalMonths: number;
  totalDonated: number;
  createdAt: string;
}

interface PledgeHistoryItem {
  id: number;
  donationId?: number;
  donationNum?: string;
  monthYear: string;
  reminderSentAt?: string;
  paidAt?: string;
  amount?: number;
  status: string;
  paymentStatus?: string;
}

interface PledgeDetailResponse {
  pledge: MonthlyPledge;
  history: PledgeHistoryItem[];
}

export default function AdminPledgeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pledge, setPledge] = useState<MonthlyPledge | null>(null);
  const [history, setHistory] = useState<PledgeHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingAmount, setEditingAmount] = useState(false);
  const [newAmount, setNewAmount] = useState('');

  useEffect(() => {
    if (!id) return;
    fetchPledge();
  }, [id]);

  const fetchPledge = async () => {
    setLoading(true);
    const res = await apiFetch<PledgeDetailResponse>(`/api/admin/monthly-pledges/${id}`);
    if (res.success && res.data) {
      setPledge(res.data.pledge);
      setHistory(res.data.history);
    }
    setLoading(false);
  };

  const handleSendReminder = async () => {
    if (!pledge) return;
    if (!confirm('確定要發送提醒給此月捐計劃的捐款人？')) return;
    setSaving(true);
    const res = await apiFetch<{ donationNum: string; sent: boolean }>(`/api/admin/monthly-pledges/${id}/remind`, {
      method: 'POST',
    });
    setSaving(false);
    if (res.success) {
      alert('提醒已發送！');
      fetchPledge();
    } else {
      alert(res.error?.message || '發送失敗');
    }
  };

  const handlePause = async () => {
    if (!pledge) return;
    if (!confirm('確定要暫停此月捐計劃？')) return;
    setSaving(true);
    const res = await apiFetch(`/api/admin/monthly-pledges/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'paused', paused_reason: '管理員手動暫停' }),
    });
    setSaving(false);
    if (res.success) {
      alert('計劃已暫停');
      fetchPledge();
    } else {
      alert(res.error?.message || '操作失敗');
    }
  };

  const handleResume = async () => {
    if (!pledge) return;
    if (!confirm('確定要恢復此月捐計劃？')) return;
    setSaving(true);
    const res = await apiFetch(`/api/admin/monthly-pledges/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'active' }),
    });
    setSaving(false);
    if (res.success) {
      alert('計劃已恢復');
      fetchPledge();
    } else {
      alert(res.error?.message || '操作失敗');
    }
  };

  const handleCancel = async () => {
    if (!pledge) return;
    if (!confirm('確定要取消此月捐計劃？此操作無法撤銷。')) return;
    setSaving(true);
    const res = await apiFetch(`/api/admin/monthly-pledges/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'cancelled', cancelled_reason: '管理員手動取消' }),
    });
    setSaving(false);
    if (res.success) {
      alert('計劃已取消');
      fetchPledge();
    } else {
      alert(res.error?.message || '操作失敗');
    }
  };

  const handleUpdateAmount = async () => {
    if (!pledge || !newAmount) return;
    const amountCents = Math.round(parseFloat(newAmount) * 100);
    if (Number.isNaN(amountCents) || amountCents <= 0) {
      alert('請輸入有效的金額');
      return;
    }
    if (!confirm(`確定要將每月金額修改為 HK$${(amountCents / 100).toLocaleString()} 嗎？`)) return;
    setSaving(true);
    const res = await apiFetch(`/api/admin/monthly-pledges/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ monthly_amount: amountCents }),
    });
    setSaving(false);
    if (res.success) {
      alert('金額已更新');
      setEditingAmount(false);
      fetchPledge();
    } else {
      alert(res.error?.message || '更新失敗');
    }
  };

  const statusConfig: Record<string, { label: string; className: string; icon: React.ElementType }> = {
    active: { label: '進行中', className: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
    paused: { label: '已暫停', className: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: AlertTriangle },
    cancelled: { label: '已取消', className: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle },
    pending: { label: '待確認', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertCircle },
  };

  const methodLabel = (m: string) => {
    const labels: Record<string, string> = {
      fps: 'FPS',
      payme: 'PayMe',
      'bank-transfer': '銀行轉賬',
      'credit-card': '信用卡',
      other: '其他',
    };
    return labels[m] || m;
  };

  const historyStatusIcon = (item: PledgeHistoryItem) => {
    if (item.paymentStatus === 'confirmed' || item.status === 'paid') {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    if (item.paymentStatus === 'failed' || item.status === 'failed') {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    if (item.reminderSentAt && !item.paidAt) {
      return <Bell className="w-4 h-4 text-amber-500" />;
    }
    return <Clock className="w-4 h-4 text-gray-400" />;
  };

  const historyStatusLabel = (item: PledgeHistoryItem) => {
    if (item.paymentStatus === 'confirmed' || item.status === 'paid') return '已付款';
    if (item.paymentStatus === 'failed' || item.status === 'failed') return '失敗';
    if (item.reminderSentAt && !item.paidAt) return '已提醒';
    return '待處理';
  };

  const historyStatusClass = (item: PledgeHistoryItem) => {
    if (item.paymentStatus === 'confirmed' || item.status === 'paid') return 'text-green-700 bg-green-50';
    if (item.paymentStatus === 'failed' || item.status === 'failed') return 'text-red-700 bg-red-50';
    if (item.reminderSentAt && !item.paidAt) return 'text-amber-700 bg-amber-50';
    return 'text-gray-600 bg-gray-50';
  };

  const paidCount = history.filter(
    (h) => h.paymentStatus === 'confirmed' || h.status === 'paid'
  ).length;
  const failedCount = history.filter(
    (h) => h.paymentStatus === 'failed' || h.status === 'failed'
  ).length;
  const totalRecords = history.length;
  const completionRate = totalRecords > 0 ? Math.round((paidCount / totalRecords) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  if (!pledge) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">找不到月捐計劃記錄</p>
        <button onClick={() => navigate('/admin/monthly-pledges')} className="btn-secondary">
          <ArrowLeft className="w-4 h-4" />
          返回月捐列表
        </button>
      </div>
    );
  }

  const status = statusConfig[pledge.status];
  const StatusIcon = status.icon;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin/monthly-pledges')}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarHeart className="w-7 h-7 text-brand-600" />
            月捐計劃詳情
          </h1>
          <p className="text-sm text-gray-500 font-mono">{pledge.pledgeNum}</p>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`flex items-center gap-3 px-5 py-4 rounded-2xl border ${status.className}`}>
        <StatusIcon className="w-6 h-6" />
        <div>
          <div className="font-bold text-lg">{status.label}</div>
          <div className="text-sm opacity-80">
            {pledge.status === 'active' && '計劃正在進行中，系統會按時發送提醒'}
            {pledge.status === 'paused' && '計劃已暫停，不會發送新的提醒'}
            {pledge.status === 'cancelled' && '計劃已被取消'}
            {pledge.status === 'pending' && '等待管理員確認'}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4 flex flex-col items-center text-center">
          <div className="text-sm text-gray-500 mb-1">總期數</div>
          <div className="text-2xl font-extrabold text-gray-900">{totalRecords}</div>
        </div>
        <div className="card p-4 flex flex-col items-center text-center">
          <div className="text-sm text-gray-500 mb-1">已成功</div>
          <div className="text-2xl font-extrabold text-green-600">{paidCount}</div>
        </div>
        <div className="card p-4 flex flex-col items-center text-center">
          <div className="text-sm text-gray-500 mb-1">失敗</div>
          <div className="text-2xl font-extrabold text-red-600">{failedCount}</div>
        </div>
        <div className="card p-4 flex flex-col items-center text-center">
          <div className="text-sm text-gray-500 mb-1">完成度</div>
          <div className="text-2xl font-extrabold text-brand-600">{completionRate}%</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">付款進度</span>
          <span className="text-sm font-medium text-gray-700">{paidCount} / {totalRecords} 期</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <div
            className="bg-brand-500 h-3 rounded-full transition-all"
            style={{ width: `${totalRecords > 0 ? (paidCount / totalRecords) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Donor Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6 space-y-5">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-brand-600" />
              捐款人資料
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <User className="w-4 h-4" /> 姓名
                </span>
                <span className="font-medium text-gray-900">{pledge.donorName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Phone className="w-4 h-4" /> 電話
                </span>
                <span className="font-medium text-gray-900">{pledge.donorPhone}</span>
              </div>
              {pledge.donorEmail && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 flex items-center gap-1.5">
                    <Mail className="w-4 h-4" /> 電郵
                  </span>
                  <span className="font-medium text-gray-900">{pledge.donorEmail}</span>
                </div>
              )}
            </div>
          </div>

          {/* Plan Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6 space-y-5">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-brand-600" />
              計劃詳情
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <CircleDollarSign className="w-4 h-4" /> 每月金額
                </span>
                {editingAmount ? (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">HK$</span>
                      <input
                        type="number"
                        value={newAmount}
                        onChange={(e) => setNewAmount(e.target.value)}
                        className="pl-10 pr-2 py-1.5 w-28 rounded-lg border border-gray-200 text-sm font-semibold text-gray-900 outline-none focus:border-brand-400"
                        placeholder="金額"
                        autoFocus
                      />
                    </div>
                    <button
                      onClick={handleUpdateAmount}
                      disabled={saving}
                      className="px-3 py-1.5 bg-brand-600 text-white text-xs font-bold rounded-lg hover:bg-brand-700 disabled:opacity-50"
                    >
                      保存
                    </button>
                    <button
                      onClick={() => { setEditingAmount(false); setNewAmount(''); }}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-200"
                    >
                      取消
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-extrabold text-brand-600">
                      HK${(pledge.monthlyAmount / 100).toLocaleString()}
                    </span>
                    <button
                      onClick={() => { setEditingAmount(true); setNewAmount(String(pledge.monthlyAmount / 100)); }}
                      className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                      title="修改金額"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Banknote className="w-4 h-4" /> 付款方式
                </span>
                <span className="font-medium text-gray-900">{methodLabel(pledge.paymentMethod)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" /> 開始日期
                </span>
                <span className="font-medium text-gray-900">
                  {new Date(pledge.startDate).toLocaleDateString('zh-HK')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> 已捐月數
                </span>
                <span className="font-medium text-gray-900">{pledge.totalMonths} 個月</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <CircleDollarSign className="w-4 h-4" /> 累計捐款
                </span>
                <span className="font-medium text-gray-900">
                  HK${(pledge.totalDonated / 100).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" /> 下次提醒
                </span>
                <span className="font-medium text-gray-900">
                  {pledge.nextReminderDate
                    ? new Date(pledge.nextReminderDate).toLocaleDateString('zh-HK')
                    : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" /> 建立時間
                </span>
                <span className="font-medium text-gray-900">
                  {new Date(pledge.createdAt).toLocaleString('zh-HK')}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          {pledge.status === 'active' && (
            <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6 space-y-3">
              <h2 className="font-bold text-gray-900 mb-2">操作</h2>
              <button
                onClick={handleSendReminder}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 btn-primary py-3 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Bell className="w-5 h-5" />}
                發送提醒
              </button>
              <button
                onClick={handlePause}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 font-bold py-3 px-6 rounded-xl transition-all border border-yellow-200 disabled:opacity-50"
              >
                <AlertTriangle className="w-5 h-5" />
                暫停計劃
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 font-bold py-3 px-6 rounded-xl transition-all border border-red-200 disabled:opacity-50"
              >
                <XCircle className="w-5 h-5" />
                取消計劃
              </button>
            </div>
          )}

          {pledge.status === 'paused' && (
            <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6 space-y-3">
              <h2 className="font-bold text-gray-900 mb-2">操作</h2>
              <button
                onClick={handleResume}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 btn-primary py-3 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                恢復計劃
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 font-bold py-3 px-6 rounded-xl transition-all border border-red-200 disabled:opacity-50"
              >
                <XCircle className="w-5 h-5" />
                取消計劃
              </button>
            </div>
          )}

          {pledge.status === 'pending' && (
            <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6 space-y-3">
              <h2 className="font-bold text-gray-900 mb-2">操作</h2>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 font-bold py-3 px-6 rounded-xl transition-all border border-red-200 disabled:opacity-50"
              >
                <XCircle className="w-5 h-5" />
                取消計劃
              </button>
            </div>
          )}
        </div>

        {/* Right Column: History */}
        <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6">
          <h2 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-brand-600" />
            捐款歷史
          </h2>

          {history.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Clock className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              暫無歷史記錄
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:border-brand-200 transition-colors"
                >
                  <div className="mt-0.5">{historyStatusIcon(item)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-900 text-sm">{item.monthYear}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${historyStatusClass(item)}`}>
                        {historyStatusLabel(item)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                      {item.donationNum && (
                        <div>捐款編號：{item.donationNum}</div>
                      )}
                      {item.amount !== undefined && (
                        <div>金額：HK${(item.amount / 100).toLocaleString()}</div>
                      )}
                      {item.reminderSentAt && (
                        <div>提醒發送：{new Date(item.reminderSentAt).toLocaleString('zh-HK')}</div>
                      )}
                      {item.paidAt && (
                        <div>付款時間：{new Date(item.paidAt).toLocaleString('zh-HK')}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
